import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Clock, AlertTriangle, RefreshCw, CheckCircle2, XCircle, Eye,
  Filter as FilterIcon, MailWarning, Lightbulb,
} from 'lucide-react';

// ──────────────────────────────────────────────────────────────
// READ-ONLY visibility surface for Support follow-ups + Suggested actions.
// No mutation endpoints are called from this page.
// ──────────────────────────────────────────────────────────────

const FU_FILTERS = [
  { key: 'all',       label: 'All',        icon: Eye, tone: 'slate' },
  { key: 'pending',   label: 'Pending',    icon: Clock, tone: 'amber' },
  { key: 'sent',      label: 'Sent',       icon: CheckCircle2, tone: 'emerald' },
  { key: 'overdue',   label: 'Overdue',    icon: AlertTriangle, tone: 'rose' },
  { key: 'expired',   label: 'Expired',    icon: MailWarning, tone: 'rose' },
  { key: 'missed',    label: 'Missed',     icon: AlertTriangle, tone: 'rose' },
  { key: 'cancelled', label: 'Cancelled',  icon: XCircle, tone: 'slate' },
];

const SA_FILTERS = [
  { key: 'all',      label: 'All',       icon: Eye },
  { key: 'pending',  label: 'Pending',   icon: Clock },
  { key: 'accepted', label: 'Accepted',  icon: CheckCircle2 },
  { key: 'rejected', label: 'Rejected',  icon: XCircle },
  { key: 'modified', label: 'Modified',  icon: RefreshCw },
  { key: 'expired',  label: 'Expired',   icon: MailWarning },
];

const STATUS_TONE = {
  pending:   'bg-amber-500/15 text-amber-300 border-amber-500/30',
  sent:      'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  expired:   'bg-rose-500/15 text-rose-300 border-rose-500/30',
  missed:    'bg-rose-500/15 text-rose-300 border-rose-500/30',
  cancelled: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  accepted:  'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  rejected:  'bg-rose-500/15 text-rose-300 border-rose-500/30',
  modified:  'bg-blue-500/15 text-blue-300 border-blue-500/30',
};

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

// ── Time-waited helpers (display-only, no mutation) ──────────────
const HOUR = 3600 * 1000;
const DAY = 24 * HOUR;

const _delta = (iso) => {
  if (!iso) return null;
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return Date.now() - t; // positive = past, negative = future
  } catch { return null; }
};

const _humanize = (ms) => {
  const abs = Math.abs(ms);
  if (abs < HOUR) return `${Math.max(1, Math.round(abs / 60000))}m`;
  if (abs < DAY) return `${Math.max(1, Math.round(abs / HOUR))}h`;
  return `${Math.max(1, Math.round(abs / DAY))}d`;
};

// Returns {label, tone, rank} for a follow-up row.
// rank lower = more urgent (used for sort).
const followupTime = (r) => {
  const status = (r.status || '').toLowerCase();
  if (status === 'expired') {
    const d = _delta(r.expired_at || r.scheduled_at);
    return d != null
      ? { label: `expired ${_humanize(d)} ago`, tone: 'rose', rank: 1 }
      : { label: 'expired · time unknown', tone: 'rose', rank: 1 };
  }
  if (status === 'missed') {
    const d = _delta(r.scheduled_at);
    return d != null
      ? { label: `missed ${_humanize(d)} ago`, tone: 'rose', rank: 2 }
      : { label: 'missed · time unknown', tone: 'rose', rank: 2 };
  }
  if (status === 'pending') {
    const d = _delta(r.scheduled_at);
    if (d == null) return { label: 'pending · time unknown', tone: 'amber', rank: 4 };
    if (r.overdue || d > 0) {
      return { label: `overdue ${_humanize(d)}`, tone: 'rose', rank: 3 };
    }
    return { label: `due in ${_humanize(d)}`, tone: 'amber', rank: 4 };
  }
  if (status === 'sent') {
    const d = _delta(r.sent_at);
    return d != null
      ? { label: `sent ${_humanize(d)} ago`, tone: 'emerald', rank: 5 }
      : { label: 'sent · time unknown', tone: 'emerald', rank: 5 };
  }
  if (status === 'cancelled') {
    const d = _delta(r.scheduled_at || r.created_at);
    return d != null
      ? { label: `cancelled ${_humanize(d)} ago`, tone: 'slate', rank: 6 }
      : { label: 'cancelled · time unknown', tone: 'slate', rank: 6 };
  }
  return { label: 'time unknown', tone: 'slate', rank: 9 };
};

const suggestedTime = (r) => {
  const status = (r.status || '').toLowerCase();
  if (status === 'pending') {
    const d = _delta(r.created_at);
    return d != null
      ? { label: `pending ${_humanize(d)}`, tone: 'amber', rank: 1 }
      : { label: 'pending · time unknown', tone: 'amber', rank: 1 };
  }
  if (status === 'expired') {
    const d = _delta(r.expired_at || r.expires_at);
    return d != null
      ? { label: `expired ${_humanize(d)} ago`, tone: 'rose', rank: 2 }
      : { label: 'expired · time unknown', tone: 'rose', rank: 2 };
  }
  if (status === 'accepted') {
    const d = _delta(r.reviewed_at);
    return d != null
      ? { label: `accepted ${_humanize(d)} ago`, tone: 'emerald', rank: 4 }
      : { label: 'accepted · time unknown', tone: 'emerald', rank: 4 };
  }
  if (status === 'rejected') {
    const d = _delta(r.reviewed_at);
    return d != null
      ? { label: `rejected ${_humanize(d)} ago`, tone: 'rose', rank: 3 }
      : { label: 'rejected · time unknown', tone: 'rose', rank: 3 };
  }
  if (status === 'modified') {
    const d = _delta(r.reviewed_at);
    return d != null
      ? { label: `modified ${_humanize(d)} ago`, tone: 'blue', rank: 5 }
      : { label: 'modified · time unknown', tone: 'blue', rank: 5 };
  }
  return { label: 'time unknown', tone: 'slate', rank: 9 };
};

const TONE_CLASS = {
  rose:    'bg-rose-500/15 text-rose-300 border-rose-500/30',
  amber:   'bg-amber-500/15 text-amber-300 border-amber-500/30',
  emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  blue:    'bg-blue-500/15 text-blue-300 border-blue-500/30',
  slate:   'bg-slate-500/15 text-slate-300 border-slate-500/30',
};

export default function SupportVisibility() {
  const [tab, setTab] = useState('followups'); // 'followups' | 'suggestions'

  // Follow-ups state
  const [fuItems, setFuItems] = useState([]);
  const [fuCounts, setFuCounts] = useState({});
  const [fuFilter, setFuFilter] = useState('all');
  const [fuLoading, setFuLoading] = useState(false);

  // Suggested-actions state
  const [saItems, setSaItems] = useState([]);
  const [saCounts, setSaCounts] = useState({});
  const [saFilter, setSaFilter] = useState('all');
  const [saLoading, setSaLoading] = useState(false);

  const loadFollowups = async (status = fuFilter) => {
    setFuLoading(true);
    try {
      const [list, counts] = await Promise.all([
        axios.get(`${API}/support-v2/staff/followups`, { params: { status }, withCredentials: true }),
        axios.get(`${API}/support-v2/staff/followups/counts`, { withCredentials: true }),
      ]);
      setFuItems(list.data?.followups || []);
      setFuCounts(counts.data?.counts || {});
    } catch (e) {
      toast.error(`Failed to load follow-ups: ${e?.response?.data?.detail || e.message}`);
    } finally { setFuLoading(false); }
  };

  const loadSuggestions = async (status = saFilter) => {
    setSaLoading(true);
    try {
      const [list, counts] = await Promise.all([
        axios.get(`${API}/support-v2/staff/suggested-actions`, { params: { status }, withCredentials: true }),
        axios.get(`${API}/support-v2/staff/suggested-actions/counts`, { withCredentials: true }),
      ]);
      setSaItems(list.data?.suggested_actions || []);
      setSaCounts(counts.data?.counts || {});
    } catch (e) {
      toast.error(`Failed to load suggested actions: ${e?.response?.data?.detail || e.message}`);
    } finally { setSaLoading(false); }
  };

  useEffect(() => {
    if (tab === 'followups') loadFollowups(fuFilter);
    else loadSuggestions(saFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => { if (tab === 'followups') loadFollowups(fuFilter); /* eslint-disable-next-line */ }, [fuFilter]);
  useEffect(() => { if (tab === 'suggestions') loadSuggestions(saFilter); /* eslint-disable-next-line */ }, [saFilter]);

  const fuVisible = useMemo(() => {
    // Urgency sort: lower rank = more urgent. Stable tie-break by scheduled_at desc.
    const decorated = (fuItems || []).map(r => ({ row: r, t: followupTime(r) }));
    decorated.sort((a, b) => {
      if (a.t.rank !== b.t.rank) return a.t.rank - b.t.rank;
      const ax = a.row.scheduled_at || a.row.created_at || '';
      const bx = b.row.scheduled_at || b.row.created_at || '';
      return bx.localeCompare(ax);
    });
    return decorated;
  }, [fuItems]);

  const saVisible = useMemo(() => {
    const decorated = (saItems || []).map(r => ({ row: r, t: suggestedTime(r) }));
    decorated.sort((a, b) => {
      if (a.t.rank !== b.t.rank) return a.t.rank - b.t.rank;
      const ax = a.row.created_at || '';
      const bx = b.row.created_at || '';
      return bx.localeCompare(ax);
    });
    return decorated;
  }, [saItems]);

  return (
    <AdminLayout>
      <div className="px-8 py-8 max-w-[1300px] mx-auto" data-testid="support-visibility-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <FilterIcon className="w-5 h-5 text-amber-400" />
              <h1 className="text-2xl font-medium text-white">Support Visibility</h1>
              <Badge className="bg-slate-700 text-slate-300 text-xs">Read-only</Badge>
            </div>
            <p className="text-sm text-slate-400 max-w-2xl">
              Scoped view of support follow-ups and suggested actions across all your assigned subscribers.
              No actions are taken from this page — use the existing Support Dashboard or Autonomy Monitoring screen
              to act on individual records.
            </p>
          </div>
          <Button
            data-testid="visibility-refresh-btn"
            variant="outline"
            className="border-slate-700 text-slate-200 hover:bg-slate-800"
            onClick={() => tab === 'followups' ? loadFollowups(fuFilter) : loadSuggestions(saFilter)}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${fuLoading || saLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-800 pb-2">
          <button
            data-testid="tab-followups"
            onClick={() => setTab('followups')}
            className={`px-3 py-1.5 text-sm rounded-md transition ${tab === 'followups' ? 'bg-amber-500/15 text-amber-300' : 'text-slate-400 hover:text-slate-200'}`}
          >
            <Clock className="w-4 h-4 inline mr-1.5" />
            Follow-ups
          </button>
          <button
            data-testid="tab-suggestions"
            onClick={() => setTab('suggestions')}
            className={`px-3 py-1.5 text-sm rounded-md transition ${tab === 'suggestions' ? 'bg-amber-500/15 text-amber-300' : 'text-slate-400 hover:text-slate-200'}`}
          >
            <Lightbulb className="w-4 h-4 inline mr-1.5" />
            Suggested actions
          </button>
        </div>

        {/* FOLLOW-UPS TAB */}
        {tab === 'followups' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-4">
              {FU_FILTERS.map(f => {
                const Icon = f.icon;
                const count = f.key === 'all' ? (fuCounts.total || 0) : (fuCounts[f.key] || 0);
                const active = fuFilter === f.key;
                return (
                  <button
                    key={f.key}
                    data-testid={`fu-filter-${f.key}`}
                    onClick={() => setFuFilter(f.key)}
                    className={`text-left rounded-lg border p-3 transition ${
                      active ? 'border-amber-500/40 bg-amber-500/5' : 'border-slate-800 hover:border-slate-700 bg-slate-900/40'
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 mb-1 ${active ? 'text-amber-300' : 'text-slate-500'}`} />
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{f.label}</div>
                    <div className="text-xl text-white">{count}</div>
                  </button>
                );
              })}
            </div>

            <Card className="bg-slate-900/40 border-slate-800">
              <CardContent className="p-0">
                {fuLoading ? (
                  <div className="text-center py-12 text-slate-500">Loading…</div>
                ) : fuVisible.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    No follow-ups in this filter / scope.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-800">
                    {fuVisible.map(({ row: r, t }) => (
                      <div
                        key={r.id}
                        data-testid={`fu-row-${r.status}`}
                        className="flex items-start gap-4 px-5 py-4"
                      >
                        <Badge className={`${STATUS_TONE[r.status] || STATUS_TONE.cancelled} border text-[10px] uppercase tracking-wider`}>
                          {r.status}{r.overdue && r.status === 'pending' ? ' • overdue' : ''}
                        </Badge>
                        <Badge
                          data-testid={`fu-time-${r.id}`}
                          className={`${TONE_CLASS[t.tone] || TONE_CLASS.slate} border text-[10px] tracking-wide whitespace-nowrap`}
                          title={`Scheduled: ${fmt(r.scheduled_at)} · Sent: ${fmt(r.sent_at)} · Expired: ${fmt(r.expired_at)}`}
                        >
                          {t.label}
                        </Badge>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white text-sm font-medium truncate">
                              {r.subscriber_name || r.subscriber_id}
                            </span>
                            {r.subscriber_is_demo && (
                              <Badge className="bg-amber-500/15 text-amber-300 border-0 text-[8px] px-1.5">DEMO</Badge>
                            )}
                            {r.ticket_subject && (
                              <span className="text-xs text-slate-400 truncate">· {r.ticket_subject}</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-300 mb-1.5 line-clamp-2">{r.message || '—'}</div>
                          <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-0.5">
                            <span>type: <code className="text-slate-300">{r.followup_type || '—'}</code></span>
                            <span>scheduled: <code className="text-slate-300">{fmt(r.scheduled_at)}</code></span>
                            {r.sent_at && <span>sent: <code className="text-slate-300">{fmt(r.sent_at)}</code></span>}
                            {r.expired_at && <span>expired: <code className="text-slate-300">{fmt(r.expired_at)}</code></span>}
                            {r.responded_at && <span>responded: <code className="text-slate-300">{fmt(r.responded_at)}</code></span>}
                            {r.ticket_id && <span>ticket: <code className="text-slate-300">{r.ticket_id}</code></span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* SUGGESTED ACTIONS TAB */}
        {tab === 'suggestions' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
              {SA_FILTERS.map(f => {
                const Icon = f.icon;
                const count = f.key === 'all' ? (saCounts.total || 0) : (saCounts[f.key] || 0);
                const active = saFilter === f.key;
                return (
                  <button
                    key={f.key}
                    data-testid={`sa-filter-${f.key}`}
                    onClick={() => setSaFilter(f.key)}
                    className={`text-left rounded-lg border p-3 transition ${
                      active ? 'border-amber-500/40 bg-amber-500/5' : 'border-slate-800 hover:border-slate-700 bg-slate-900/40'
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 mb-1 ${active ? 'text-amber-300' : 'text-slate-500'}`} />
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{f.label}</div>
                    <div className="text-xl text-white">{count}</div>
                  </button>
                );
              })}
            </div>

            <Card className="bg-slate-900/40 border-slate-800">
              <CardContent className="p-0">
                {saLoading ? (
                  <div className="text-center py-12 text-slate-500">Loading…</div>
                ) : saVisible.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    No suggested actions in this filter / scope.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-800">
                    {saVisible.map(({ row: r, t }) => (
                      <div
                        key={r.id}
                        data-testid={`sa-row-${r.status}`}
                        className="flex items-start gap-4 px-5 py-4"
                      >
                        <Badge className={`${STATUS_TONE[r.status] || STATUS_TONE.cancelled} border text-[10px] uppercase tracking-wider`}>
                          {r.status}
                        </Badge>
                        <Badge
                          data-testid={`sa-time-${r.id}`}
                          className={`${TONE_CLASS[t.tone] || TONE_CLASS.slate} border text-[10px] tracking-wide whitespace-nowrap`}
                          title={`Created: ${fmt(r.created_at)} · Expires: ${fmt(r.expires_at)} · Reviewed: ${fmt(r.reviewed_at)}`}
                        >
                          {t.label}
                        </Badge>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white text-sm font-medium truncate">
                              {r.subscriber_name || r.subscriber_id}
                            </span>
                            {r.subscriber_is_demo && (
                              <Badge className="bg-amber-500/15 text-amber-300 border-0 text-[8px] px-1.5">DEMO</Badge>
                            )}
                            {r.action_type && (
                              <span className="text-xs text-slate-400 truncate">· {r.action_type}</span>
                            )}
                            {r.confidence_label && (
                              <Badge className="bg-slate-700/60 text-slate-300 border-0 text-[8px] px-1.5">
                                {r.confidence_label}
                              </Badge>
                            )}
                          </div>
                          {r.suggested_change && (
                            <div className="text-xs text-slate-300 mb-1.5 line-clamp-2">{r.suggested_change}</div>
                          )}
                          {r.reason && (
                            <div className="text-[11px] text-slate-400 mb-1.5">
                              <span className="text-slate-500">reason:</span> {r.reason}
                            </div>
                          )}
                          <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-0.5">
                            <span>created: <code className="text-slate-300">{fmt(r.created_at)}</code></span>
                            {r.expires_at && <span>expires: <code className="text-slate-300">{fmt(r.expires_at)}</code></span>}
                            {r.expired_at && <span>expired: <code className="text-slate-300">{fmt(r.expired_at)}</code></span>}
                            {r.reviewed_at && <span>reviewed: <code className="text-slate-300">{fmt(r.reviewed_at)}</code></span>}
                            {r.ticket_id && <span>ticket: <code className="text-slate-300">{r.ticket_id}</code></span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
